import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import useAuth from "../../../hooks/useAuth";
import useLogout from "../../../hooks/useLogout";
import UseAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useSocket from "../../../hooks/useWebsocket";
import { useSIP } from "../../../context/SIPContext";
import { BREAK_REASONS, AGENT_ACTIVITY } from "../../../utils/ApiUrls";
import { initialsOf, STATUS_COLOR_VARS, STATUS_LABELS } from "./utils";
import useFetch from "../../../hooks/useFetch";


const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap";

// Drives the app-wide Header (src/pages/Agent/Dashboard2/Header.js) from
// PersistLogin, so the header — SIP state, campaign switching, profile menu —
// stays mounted and untouched while individual pages swap in and out.
const useAgentHeader = ({ onlineStatus, setOnlineStatus, onlineStatusColour, setOnlineStatusColour }) => {
  const { auth } = useAuth();
  const axiosPrivate = UseAxiosPrivate("header");
  const logout = useLogout();
  const fetchData = useFetch();

  const { status, callStatus, updateConfig, connect, unregister, openDialer } = useSIP();
  const [, , , , isOnPbxCall, setIsOnPbxCall] = useSocket();
  // Only restrict switching when we positively know the agent is on a call.
  // An unknown/unavailable status must not block switching.
  const ACTIVE_CALL_STATUSES = ["active", "calling", "incoming"];
  const isOnActiveCall = ACTIVE_CALL_STATUSES.includes(callStatus) || !!isOnPbxCall;

  const type = auth?.roles?.includes("sys_admin") ? "admin" : "agent";
  const displayName = type === "agent" ? auth?.user_details?.profile_name : auth?.user_details?.username;
  const initials = initialsOf(displayName);
  const todaysDate = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  // load the mockup's fonts once, without touching the global index.html
  useEffect(() => {
    if (document.querySelector(`link[href="${FONT_HREF}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);

  const [profileOpen, setProfileOpen] = useState(false);
  const closeAllPopovers = () => setProfileOpen(false);

  const permissions = auth?.permissions?.data || [];
  const hasWebRtc =
    permissions.some((p) => p.name === "WebRtc") ||
    (type === "admin" && !!auth?.user_details?.sip_details);
  const hasLoginPermission = permissions.some((p) => p.name === "Agent User Login");

  const signout = async () => {
    try {
      unregister();
      if (!hasLoginPermission) {
        await logout("instant_logout");
      } else {
        await logout();
      }
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  useEffect(() => {
    if (!hasLoginPermission && type === "agent") {
      Swal.fire({
        title: "Login Failed",
        text: "User is not allowed to login. Please Contact Admin to get access.",
        confirmButtonColor: "#3085d6",
        icon: "error",
      });
      signout();
    }
  }, [hasLoginPermission, type]);

  // ---- SIP registration ----
  const sip_details = auth?.user_details?.sip_details;
  const [sipDetails, setSipDetails] = useState(sip_details);
  const [formConfig, setFormConfig] = useState({
    username: sipDetails?.sip_id || "",
    password: sipDetails?.password || "",
    domain: sipDetails?.domain || "",
    webrtcUrl: sipDetails?.proxy_url || "",
  });
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // auth (and therefore sip_details) loads asynchronously after mount on a
  // refresh, so seed sipDetails/formConfig here rather than only at
  // useState() init time above, which would otherwise stay stuck empty.
  useEffect(() => {
    if (sip_details && !sipDetails) {
      setSipDetails(sip_details);
      setFormConfig({
        username: sip_details.sip_id || "",
        password: sip_details.password || "",
        domain: sip_details.domain || "",
        webrtcUrl: sip_details.proxy_url || "",
      });
    }
  }, [sip_details, sipDetails]);

  const autoConnectAttemptedRef = useRef(false);
  useEffect(() => {
    if (autoConnectAttemptedRef.current) return;
    const userWantsSip = localStorage.getItem("sipEnabled") === "true";
    if (
      hasWebRtc &&
      userWantsSip &&
      (sipDetails || sip_details) &&
      onlineStatus === "available"
    ) {
      autoConnectAttemptedRef.current = true;
      updateConfig(formConfig);
      connect();
    }
  }, [hasWebRtc, sipDetails, sip_details, onlineStatus, formConfig]);

  // Backs off reconnect attempts (2s, 4s, 8s ... capped at 30s) instead of
  // retrying immediately on every 'disconnected' event, so a persistent
  // failure (e.g. an expired server cert) doesn't hammer the PBX in a tight
  // loop. Resets once a connection actually succeeds.
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  useEffect(() => {
    if (status === "connected") {
      setIsEnabled(true);
      reconnectAttemptsRef.current = 0;
      clearTimeout(reconnectTimeoutRef.current);
    } else if (status === "disconnected") {
      const userWantsSip = localStorage.getItem("sipEnabled") === "true";
      if (userWantsSip && hasWebRtc) {
        const attempt = reconnectAttemptsRef.current;
        const delay = Math.min(2000 * 2 ** attempt, 30000);
        reconnectAttemptsRef.current = attempt + 1;
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      } else {
        setIsEnabled(false);
      }
    }
    return () => clearTimeout(reconnectTimeoutRef.current);
  }, [status]);

  const prevOnlineStatusRef = useRef(onlineStatus);
  useEffect(() => {
    const initializeSIP = async () => {
      try {
        const previousStatus = prevOnlineStatusRef.current;
        prevOnlineStatusRef.current = onlineStatus;
        // Only auto-reconnect on a genuine unavailable/break -> available
        // transition (SIP is force-disabled on those statuses).
        const justBecameAvailable = onlineStatus === "available" && previousStatus !== "available";
        const sipIsEnabled = localStorage.getItem("sipEnabled");
        if (justBecameAvailable && sipIsEnabled === "false" && hasWebRtc) {
          updateConfig(formConfig);
          await connect();
          localStorage.setItem("sipEnabled", "true");
        }
      } catch (error) {
        console.error("SIP connection error:", error);
      }
    };
    initializeSIP();
  }, [onlineStatus]);

  const handleToggle = () => {
    if (!isEnabled) {
      if (!sipDetails && !sip_details) {
        Swal.fire({
          title: "Sip Enable Failed",
          text: "Please register",
          confirmButtonColor: "#3085d6",
          icon: "warning",
        });
        return;
      }
      localStorage.setItem("sipEnabled", "true");
      updateConfig(formConfig);
      connect();
    } else {
      localStorage.setItem("sipEnabled", "false");
      setIsEnabled(false);
      unregister();
    }
  };

  const [autoAnswer, setAutoAnswer] = useState(() => localStorage.getItem("autoAnswer") === "true");
  const [autoAnswerVisible] = useState(() => localStorage.getItem("autoAnswerVisibile") === "true");

  useEffect(() => {
    if (!autoAnswerVisible) {
      localStorage.setItem("autoAnswer", "false");
    }
  }, [autoAnswerVisible]);

  const handleAutoAnswer = () => {
    const next = !autoAnswer;
    localStorage.setItem("autoAnswer", next ? "true" : "false");
    setAutoAnswer(next);
  };

  // ---- break reasons popup ----
  const [reasonPopupOpen, setReasonPopupOpen] = useState(false);
  const [breakReasons, setBreakReasons] = useState([]);

  const fetchBreakReasons = async () => {
    const adminId = auth?.user_details?.parent;
    try {
      const res = await axiosPrivate.get(BREAK_REASONS, { params: { adminId } });
      setBreakReasons(res?.data?.reasons);
      setReasonPopupOpen(true);
    } catch (err) {
      console.log(err);
      Swal.fire({
        title: "Status Change Failed",
        text: "Unable to Change Status. Please try again",
        confirmButtonColor: "#3085d6",
        icon: "warning",
      });
    }
  };

  const currentStatusKey = type === "agent" ? onlineStatusColour : "available";
  const statusLabel = STATUS_LABELS[currentStatusKey] || "Offline";
  const statusColor = STATUS_COLOR_VARS[currentStatusKey] || "var(--muted)";

  // Persisting the status change to the backend (AGENT_ACTIVITY) is already
  // handled by the useEffect in Call.js watching this same onlineStatus prop
  // — it does the POST, writes localStorage, rolls back and shows an error
  // dialog on failure. Posting here too would double-submit every status
  // change; this hook only needs to flip the shared state and drive its own
  // side effects (SIP, campaign picker).
  const handleSetAvailable = async () => {
    setProfileOpen(false);
    setOnlineStatus?.("available");
    // Don't prompt which campaign to work — just start the agent's current
    // campaign directly. Switching campaigns is still available via the
    // swap icon on the campaign pill. Refetch first since agentCampaigns
    // can be stale.
    const results = await fetchAgentCampaigns();
    const active = (results || []).filter((c) => c.campaign_status === "start");
    if (active.length === 0) return;
    const alreadyCurrent = active.some((c) => String(c.id) === String(currentCampaignId));
    if (!alreadyCurrent) {
      await handleSwitchCampaign(active[0]);
    }
  };
  const handleSetUnavailable = () => {
    setProfileOpen(false);
    setOnlineStatus?.("unavailable");
    setIsEnabled(false);
    localStorage.setItem("sipEnabled", "false");
    unregister();
    // A stale "on call" flag (e.g. a lost websocket call-end message) must
    // not survive an explicit "I'm done" signal from the agent.
    setIsOnPbxCall?.(false);
  };
  const handleSetBreak = () => {
    fetchBreakReasons();
    setProfileOpen(false);
  };
  const handleLogout = () => {
    signout();
    setIsEnabled(false);
    localStorage.setItem("sipEnabled", "false");
    unregister();
    setProfileOpen(false);
  };

  // ---- agent's campaigns + current campaign ----
  const [agentCampaigns, setAgentCampaigns] = useState([]);
  const [currentCampaignId, setCurrentCampaignId] = useState(
    () => localStorage.getItem("currentCampaignId") || null
  );
  const [isCampaignPickerOpen, setIsCampaignPickerOpen] = useState(false);
  const [switchingCampaignId, setSwitchingCampaignId] = useState(null);
  const [campaignToConfirm, setCampaignToConfirm] = useState(null);
  const [currentCampaignName, setCurrentCampaignName] = useState(null);

  const currentCampaignBase =
    agentCampaigns.find((c) => String(c.id) === String(currentCampaignId)) || null;
  const currentCampaign = currentCampaignBase
    ? { ...currentCampaignBase, name: currentCampaignName || currentCampaignBase.name }
    : currentCampaignBase;
  const activeCampaigns = agentCampaigns.filter((c) => c.campaign_status === "start");

  const fetchUserdata = () => {
    return axiosPrivate
      .post("/contactcentre/auth/agent/", {})
      .then((res) => {
        const campaignName = res.data?.user_details?.current_campaign || null;
        setCurrentCampaignName(campaignName);
        const activeStatus = res.data?.user_details?.current_active_status;
        if (activeStatus && activeStatus !== "incall" && activeStatus !== "waiting") {
          setOnlineStatus?.(activeStatus);
          setOnlineStatusColour?.(activeStatus);
        }
        return campaignName;
      })
      .catch((err) => {
        console.log(err);
        return null;
      });
  };

  const fetchAgentCampaigns = () => {
    const userId = auth?.user_details?.user;
    if (!userId) return Promise.resolve();
    const BASE_URL = process.env.REACT_APP_API_URL;
    const config = { headers: { "Content-Type": "application/json" } };

    return axios
      .get(`${BASE_URL}/contactcentre/campaign-list/`, {
        params: { user_id: userId, user_type: "agent", page: 1 },
        ...config,
      })
      .then((res) => {
        const results = res.data?.results || [];
        setAgentCampaigns(results);
        setCurrentCampaignId((prev) => {
          if (prev && results.some((c) => String(c.id) === String(prev))) return prev;
          const ongoing = results.find((c) => c.campaign_status === "start");
          return ongoing ? String(ongoing.id) : prev;
        });
        return results;
      })
      .catch((err) => {
        console.log(err);
        return [];
      });
  };

  // The backend's `current_campaign` (from /auth/agent/) is the source of
  // truth for which campaign is actually active, but it's a name, not an id
  // — while currentCampaignId is derived/cached on the frontend (see
  // fetchAgentCampaigns above) and can keep pointing at a stale campaign
  // (e.g. after a server-side reassignment) even though it still matches
  // *some* row in the list. Reconcile the id against the authoritative name
  // whenever we have both, so the picker's "Current" badge lands on the
  // right row instead of on whatever id happened to survive.
  const syncCurrentCampaign = async () => {
    const [results, campaignName] = await Promise.all([fetchAgentCampaigns(), fetchUserdata()]);
    if (campaignName && results?.length) {
      const matched = results.find((c) => c.name === campaignName);
      if (matched && String(matched.id) !== String(currentCampaignId)) {
        setCurrentCampaignId(String(matched.id));
        localStorage.setItem("currentCampaignId", String(matched.id));
      }
    }
    return results;
  };

  // Switching is only blocked while the agent is on an active call. An
  // available agent with no live call is free to switch at any time; picking
  // their already-current campaign is a no-op guarded below.
  const isSwitchDisabled = isOnActiveCall;

  const handleSwitchCampaign = async (campaign) => {
    if (isOnActiveCall) {
      Swal.fire({
        title: "Call in Progress",
        text: "You can't switch campaigns while you're on an active call. Please finish or end the current call first.",
        confirmButtonColor: "#3085d6",
        icon: "warning",
      });
      return;
    }
    if (String(campaign.id) === String(currentCampaignId)) {
      setIsCampaignPickerOpen(false);
      return;
    }
    setSwitchingCampaignId(campaign.id);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/contactcentre/update_current_campaign/`,
        { user_id: auth?.user_details?.id, camp_id: campaign.id },
        { headers: { "Content-Type": "application/json" } }
      );
      setCurrentCampaignId(String(campaign.id));
      localStorage.setItem("currentCampaignId", String(campaign.id));
      setIsCampaignPickerOpen(false);
      
      const res_data = await fetchData(
                    "post",
                    {
                      status: onlineStatus,
                      usertype: "agent",
                      reason: "",
                    },
                    AGENT_ACTIVITY
                  );
      window.dispatchEvent(new Event("refresh-call-count"));
    } catch (err) {
      console.log(err);
      Swal.fire({
        title: "Switch Campaign Failed",
        text: "Unable to switch campaign. Please try again",
        confirmButtonColor: "#3085d6",
        icon: "warning",
      });
    } finally {
      setSwitchingCampaignId(null);
    }
  };

  useEffect(() => {
    syncCurrentCampaign();
  }, [auth?.user_details?.user]);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefreshAll = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await syncCurrentCampaign();
      window.dispatchEvent(new Event("refresh-call-count"));
    } finally {
      setIsRefreshing(false);
    }
  };

  return {
    type,
    displayName,
    todaysDate,
    hasWebRtc,
    initials,
    statusColor,
    statusLabel,
    currentStatusKey,
    currentCampaign,
    isRefreshing,
    handleRefreshAll,
    isEnabled,
    handleToggle,
    autoAnswer,
    autoAnswerVisible,
    handleAutoAnswer,
    openDialer,
    profileOpen,
    setProfileOpen,
    closeAllPopovers,
    handleSetAvailable,
    handleSetUnavailable,
    handleSetBreak,
    handleLogout,
    breakReasonsPopup: {
      open: reasonPopupOpen,
      setOpen: setReasonPopupOpen,
      onlineStatus,
      setOnlineStatus,
      reasons: breakReasons,
      setOnlineStatusColour,
      setIsEnabled,
      unregister,
      setIsOnPbxCall,
    },
    sipPopup: {
      open: isSettingsOpen,
      setOpen: setIsSettingsOpen,
      formConfig,
      setFormConfig,
      updateConfig,
      connect,
      setIsEnabled,
      setSipDetails,
      sipDetails,
    },
    campaignSwitch: {
      pickerOpen: isCampaignPickerOpen,
      setPickerOpen: setIsCampaignPickerOpen,
      activeCampaigns,
      currentCampaignId,
      switchingCampaignId,
      campaignToConfirm,
      setCampaignToConfirm,
      handleSwitchCampaign,
      isOnActiveCall,
      isSwitchDisabled,
    },
  };
};

export default useAgentHeader;
